import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputAdornment from "@material-ui/core/InputAdornment";
import A_FormHelper from "../../DataDisplay/Tooltips/A_FormHelper";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      width: theme.spacing(1),
    },
    marginTop: 5,
  },
  chip: {
    margin: theme.spacing(0.25),
  },
}));

export default function A_MultiselectWithSearch(props) {
  const {
    id,
    options = [],
    defaultValue = "",
    onChange = () => {},
    label = "",
    placeholder = "",
    disabled = false,
    limitTags = 3,
    noOptionsText = "No options",
    fieldHelperText,
    required = false,
    helperText,
    error = false,
    variant = "standard",
  } = props;

  const classes = useStyles();

  const selectedValues = React.useMemo(() => {
    if (!defaultValue) return [];
    try {
      return JSON.parse(defaultValue);
    } catch (e) {
      return [];
    }
  }, [defaultValue]);

  const handleChange = (event, newValue, reason, details) => {
    let finalValues = newValue;

    if (details?.option?.value === "__all") {
      const allSelected = selectedValues.length === options.length;
      finalValues = allSelected ? [] : [...options];
    } else {
      finalValues = newValue.filter((item) => item.value !== "__all");
    }

    onChange(JSON.stringify(finalValues), id);
  };

  return (
    <FormControl
      className={classes.root}
      error={error}
      required={required}
      fullWidth
    >
      <Autocomplete
        multiple
        options={[{ label: "Select All", value: "__all" }, ...options]}
        value={selectedValues}
        onChange={handleChange}
        disableCloseOnSelect
        getOptionLabel={(opt) => opt.label || opt.value}
        getOptionSelected={(option, value) => option.value === value.value}
        // filterSelectedOptions
        limitTags={limitTags}
        disabled={disabled}
        noOptionsText={noOptionsText}
        renderOption={(option, { selected }) => {
          const isSelectAll = option.value === "__all";
          const checked = isSelectAll
            ? selectedValues.length === options.length
            : selected;

          return (
            <>
              <Checkbox
                checked={checked}
                color="primary"
                style={{ marginRight: 8 }}
              />
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{
                  style: {
                    fontWeight: isSelectAll ? 600 : 400,
                    color: isSelectAll ? "#1976d2" : "inherit",
                  },
                }}
              />
            </>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              key={option.value}
              size="small"
              label={option.label}
              {...getTagProps({ index })}
              className={classes.chip}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant={variant}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {fieldHelperText && (
                    <A_FormHelper fieldHelperText={fieldHelperText} />
                  )}
                  {params.InputProps.endAdornment}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <FormHelperText>
        {error
          ? helperText || "This field is required"
          : fieldHelperText || helperText || ""}
      </FormHelperText>
    </FormControl>
  );
}

A_MultiselectWithSearch.propTypes = {
  id: PropTypes.any,
  options: PropTypes.array,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  limitTags: PropTypes.number,
  noOptionsText: PropTypes.string,
  fieldHelperText: PropTypes.string,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  variant: PropTypes.oneOf(["standard", "filled", "outlined"]),
};
